import { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { ArrowLeft, Home, Building2, User, Mail, Lock, Eye, EyeOff, Check, ArrowRight } from 'lucide-react'
import { GOLF_ELITE_PLANS, GOLF_ELITE_FEATURES } from '../config/golfElitePlans.js'
import { signUpEmail, logInEmail } from '../lib/platformAuth.js'
import { startSubscriptionCheckout } from '../lib/platformBilling.js'
import { GeGlassStyles } from './geGlass.jsx'
import heroBg from '../assets/brand/golf-elite-hero-bg.png'
import heroLogo from '../assets/brand/golf-elite-logo-white.png'

// Golf Elite public signup — matches the approved glass/wave design: full-bleed
// hero background image, frosted-glass card, Monthly/Annual toggle, check-badge
// features, iconed glass inputs, glossy START NOW. Prices/Price IDs from config.
// Logic unchanged: Start now creates the account then opens subscription Checkout.
export default function GolfEliteSignup() {
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const [cycle, setCycle] = useState('annual')
  const [clubName, setClubName] = useState('')
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPw, setShowPw] = useState(false)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')

  const plan = GOLF_ELITE_PLANS[cycle]
  const welcome = params.get('welcome') === '1'

  const start = async (e) => {
    e.preventDefault()
    setErr('')
    if (!clubName.trim() || !name.trim() || !email.trim() || password.length < 6) {
      setErr('Fill in every field (password at least 6 characters).')
      return
    }
    if (!plan.priceId) { setErr('Plans are not configured yet.'); return }
    setBusy(true)
    try {
      try { await signUpEmail(email.trim(), password) }
      catch (e2) {
        if (e2?.code === 'auth/email-already-in-use') await logInEmail(email.trim(), password)
        else throw e2
      }
      await startSubscriptionCheckout(plan.priceId, clubName.trim())
    } catch (e3) {
      setErr(e3?.message || 'Could not start signup.')
      setBusy(false)
    }
  }

  return (
    <main className="ge-page" style={{ backgroundImage: `url(${heroBg})` }}>
      <GeGlassStyles />

      <header className="ge-hero">
        <nav className="ge-hero-nav">
          <img className="ge-logo" src={heroLogo} alt="Golf Elite" />
          <button type="button" className="ge-nav-btn" onClick={() => navigate(-1)}><ArrowLeft size={16} strokeWidth={2.4} /><span>Back</span></button>
          <button type="button" className="ge-nav-btn" onClick={() => navigate('/')}><Home size={16} strokeWidth={2.4} /><span>Home</span></button>
          <button type="button" className="ge-login-btn" onClick={() => navigate('/admin')}>LOG IN</button>
        </nav>
        <div className="ge-hero-copy">
          <h1>Start running elite tournaments today</h1>
          <p>Everything you need to run professional golf tournaments, all in one platform.</p>
        </div>
      </header>

      <section className="ge-content">
        <div className="ge-shell">
          {welcome ? <div className="ge-welcome">Subscription received — finishing setup…</div> : null}

          <div className="ge-toggle">
            {['monthly', 'annual'].map((c) => (
              <button key={c} type="button" onClick={() => setCycle(c)} className={`ge-opt ${cycle === c ? 'active' : ''}`}>
                {GOLF_ELITE_PLANS[c].label}
              </button>
            ))}
          </div>

          <div className="ge-plan">
            <div className="ge-plan-head">
              <h2 className="ge-plan-title">Golf Elite — Full Platform</h2>
              <div className="ge-price">
                <span className="ge-price-main">{plan.display}</span>
                <span className="ge-price-per">{plan.per}</span>
              </div>
            </div>
            <div className="ge-features">
              {GOLF_ELITE_FEATURES.map((f) => (
                <div className="ge-feature" key={f}><span className="ge-check"><Check size={14} strokeWidth={3} /></span><span>{f}</span></div>
              ))}
            </div>
          </div>

          <form className="ge-form" onSubmit={start}>
            <Field id="club" label="CLUB NAME" icon={<Building2 size={18} />}>
              <input id="club" className="ge-field" type="text" value={clubName} onChange={(e) => setClubName(e.target.value)} placeholder="Enter your club name" />
            </Field>
            <Field id="name" label="YOUR NAME" icon={<User size={18} />}>
              <input id="name" className="ge-field" type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Enter your name" />
            </Field>
            <Field id="email" label="EMAIL" icon={<Mail size={18} />}>
              <input id="email" className="ge-field" type="email" autoCapitalize="none" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Enter your email" />
            </Field>
            <Field id="password" label="PASSWORD" icon={<Lock size={18} />} trailing={
              <button type="button" className="ge-eye" onClick={() => setShowPw((s) => !s)} aria-label="Show password">
                {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            }>
              <input id="password" className="ge-field" type={showPw ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Create a password" />
            </Field>

            {err ? <p className="ge-err">{err}</p> : null}

            <button type="submit" className="ge-start" disabled={busy}>
              <span>{busy ? 'STARTING…' : 'START NOW'}</span>
              {busy ? null : <ArrowRight className="ge-arrow" size={22} strokeWidth={2.4} />}
            </button>
          </form>
        </div>
      </section>
    </main>
  )
}

function Field({ id, label, icon, trailing, children }) {
  return (
    <div className="ge-group">
      <label htmlFor={id}>{label}</label>
      <div className="ge-field-wrap">
        <span className="ge-field-icon">{icon}</span>
        {children}
        {trailing || null}
      </div>
    </div>
  )
}
